import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, InputGroup } from 'react-bootstrap';

const UrlMediaDialog = ({ open, onClose, onConfirm, isMultiple = false }) => {
  const [urls, setUrls] = useState(['']);
  const [touched, setTouched] = useState([]);

  useEffect(() => {
    if (open) {
      setUrls(isMultiple ? [''] : ['']);
      setTouched(isMultiple ? [false] : [false]);
    }
  }, [open, isMultiple]);

  const isValidHttpUrl = (value) => {
    try {
      const parsed = new URL(value);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch (_) {
      return false;
    }
  };

  const allValid = useMemo(() => {
    return urls.length > 0 && urls.every((u) => typeof u === 'string' && u.trim().length > 0 && isValidHttpUrl(u.trim()));
  }, [urls]);

  // Normalize common video provider URLs to embeddable forms (YouTube, Vimeo, Facebook)
  const normalizeEmbedUrl = (rawUrl) => {
    if (!rawUrl || typeof rawUrl !== 'string') return rawUrl;
    let url;
    try {
      url = new URL(rawUrl);
    } catch (_) {
      return rawUrl;
    }

    const hostname = url.hostname.replace(/^www\./, '').toLowerCase();

    const parseYouTubeTimeToSeconds = (value) => {
      if (!value) return 0;
      const str = String(value);
      if (/^\d+$/.test(str)) return parseInt(str, 10);
      const match = str.match(/(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?/i);
      if (!match) return 0;
      const hours = parseInt(match[1] || '0', 10);
      const minutes = parseInt(match[2] || '0', 10);
      const seconds = parseInt(match[3] || '0', 10);
      return hours * 3600 + minutes * 60 + seconds;
    };

    // YouTube
    if (hostname === 'youtube.com' || hostname === 'youtu.be' || hostname === 'm.youtube.com') {
      let videoId = '';
      let startSeconds = 0;
      if (hostname === 'youtu.be') {
        videoId = url.pathname.split('/').filter(Boolean)[0] || '';
        startSeconds = parseYouTubeTimeToSeconds(url.searchParams.get('t'));
      } else {
        if (url.pathname.startsWith('/watch')) {
          videoId = url.searchParams.get('v') || '';
          startSeconds = parseYouTubeTimeToSeconds(url.searchParams.get('t') || url.searchParams.get('start'));
        } else if (url.pathname.startsWith('/shorts/')) {
          const parts = url.pathname.split('/').filter(Boolean);
          videoId = parts[1] || '';
        } else if (url.pathname.startsWith('/embed/')) {
          return rawUrl;
        }
      }
      if (videoId) {
        const base = `https://www.youtube.com/embed/${videoId}`;
        return startSeconds > 0 ? `${base}?start=${startSeconds}` : base;
      }
      return rawUrl;
    }

    // Vimeo
    if (hostname === 'vimeo.com' || hostname === 'player.vimeo.com') {
      if (hostname === 'player.vimeo.com' && url.pathname.startsWith('/video/')) return rawUrl;
      const pathParts = url.pathname.split('/').filter(Boolean);
      const numericSegments = pathParts.filter((p) => /^\d+$/.test(p));
      const videoId = numericSegments.length > 0 ? numericSegments[numericSegments.length - 1] : '';
      if (videoId) {
        return `https://player.vimeo.com/video/${videoId}`;
      }
      return rawUrl;
    }

    // Facebook
    if (hostname === 'facebook.com' || hostname === 'm.facebook.com' || hostname === 'fb.watch') {
      if (hostname.includes('facebook.com') && url.pathname.startsWith('/plugins/video.php')) return rawUrl;
      const encoded = encodeURIComponent(rawUrl);
      return `https://www.facebook.com/plugins/video.php?href=${encoded}&show_text=0&width=560`;
    }

    return rawUrl;
  };

  const handleChange = (index, value) => {
    setUrls((prev) => {
      const copy = [...prev];
      copy[index] = value;
      return copy;
    });
  };

  const handleBlur = (index) => {
    setTouched((prev) => {
      const copy = [...prev];
      copy[index] = true;
      return copy;
    });
  };

  const addField = () => {
    setUrls((prev) => [...prev, '']);
    setTouched((prev) => [...prev, false]);
  };

  const removeField = (index) => {
    setUrls((prev) => prev.filter((_, i) => i !== index));
    setTouched((prev) => prev.filter((_, i) => i !== index));
  };

  const handleConfirm = () => {
    const cleaned = urls.map((u) => normalizeEmbedUrl(u.trim()));
    if (!isMultiple) {
      onConfirm([cleaned[0]]);
    } else {
      onConfirm(cleaned);
    }
    handleClose();
  };

  const handleClose = () => {
    setUrls(['']);
    setTouched([false]);
    onClose();
  };

  return (
    <Modal show={open} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{isMultiple ? 'Add Image URLs' : 'Add Image URL'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          {urls.map((value, index) => {
            const hasError = touched[index] && (value.trim().length === 0 || !isValidHttpUrl(value.trim()));
            return (
              <Form.Group className="mb-3" key={index}>
                <Form.Label>Image URL {isMultiple ? `#${index + 1}` : ''}</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="url"
                    placeholder="https://example.com/image.jpg"
                    value={value}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onBlur={() => handleBlur(index)}
                    isInvalid={hasError}
                  />
                  {isMultiple && urls.length > 1 && (
                    <Button variant="outline-danger" onClick={() => removeField(index)}>
                      Remove
                    </Button>
                  )}
                </InputGroup>
                <Form.Control.Feedback type="invalid">
                  Please enter a valid http(s) URL.
                </Form.Control.Feedback>
              </Form.Group>
            );
          })}
        </Form>
        {isMultiple && (
          <Button variant="outline-primary" onClick={addField}>
            Add another URL
          </Button>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleConfirm} disabled={!allValid}>
          Confirm
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UrlMediaDialog;


